import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { contentApi } from '../../services/contentApi';
import { CourseListItem } from '../../types/content';
import Button from '../../components/UI/Button';
import { confirmAction } from '../../utils/confirmAction';
import { isOwner } from '../../utils/roles';
import { useAuth } from '../../context/AuthContext';

const MyCoursesPage: React.FC = () => {
  const { user } = useAuth();
  const owner = isOwner(user);
  const [courses, setCourses] = useState<CourseListItem[]>([]);
  useEffect(() => {
    contentApi.myCourses().then((r) => setCourses(r.data.courses || [])).catch(() => undefined);
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">My Courses</h1>
        <Link to="/my-courses/new"><Button>New course</Button></Link>
      </div>
      <div className="mt-6 space-y-3">
        {courses.length === 0 && <p className="text-gray-500">You have not created any courses yet.</p>}
        {courses.map((course) => (
          <div key={course.id} className="surface-card surface-card-hover p-4 flex justify-between gap-3">
            <Link to={`/my-courses/${course.id}/edit`} className="flex-1">
              <div className="flex justify-between">
                <h2 className="font-semibold text-gray-900">{course.title}</h2>
                <span className="text-sm text-gray-500">{course.status}</span>
              </div>
              <p className="text-sm text-gray-600 mt-1">{course.summary || course.subtitle}</p>
              <p className="text-xs text-gray-500 mt-1">{course.isPublic === false ? 'Private' : 'Public'}{course.ratingCount ? ` · ★ ${Number(course.averageRating || 0).toFixed(1)} (${course.ratingCount})` : ''}</p>
            </Link>
            <div className="flex flex-col items-end gap-2 shrink-0">
              <button
                type="button"
                className="text-sm text-error-600"
                onClick={async () => {
                  const ok = await confirmAction({
                    title: 'Archive this course?',
                    description: `“${course.title}” will be deactivated (soft delete).`,
                    confirmLabel: 'Archive',
                    cancelLabel: 'Keep it',
                    variant: 'danger',
                  });
                  if (!ok) return;
                  contentApi.archiveCourse(course.id).then(() => setCourses((current) => current.filter((item) => item.id !== course.id)));
                }}
              >
                Archive
              </button>
              {owner ? (
                <button
                  type="button"
                  className="text-sm text-error-700 font-medium"
                  onClick={async () => {
                    const ok = await confirmAction({
                      title: 'Delete course permanently?',
                      description: `“${course.title}” will be permanently removed. This cannot be undone.`,
                      confirmLabel: 'Delete permanently',
                      cancelLabel: 'Cancel',
                      variant: 'danger',
                    });
                    if (!ok) return;
                    contentApi.permanentDeleteCourse(course.id).then(() => setCourses((current) => current.filter((item) => item.id !== course.id)));
                  }}
                >
                  Delete permanently
                </button>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MyCoursesPage;
